import React from 'react';
import { useQuery } from 'react-query';

import { Form } from 'antd';
import styled from 'styled-components';

import { getHoldings } from 'apis/HoldingAPI';
import Checkbox from 'components/lib/Checkbox';
import GradientDivider from 'components/lib/GradientDivider';
import Input from 'components/lib/Input';
import Select from 'components/lib/Select';
import type { PositionRightDataType } from 'store/slices/globalSlice';
import { HoldingType } from 'types/holding';

import UserManagementPositionPermissions from './UserManagementPositionPermissions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  overflow: hidden;
`;

const Header = styled.div`
  font-size: 18px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  padding-top: 16px;
  padding-left: 30px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 30px;
`;

const BaselineFieldRow = styled(FieldRow)`
  align-items: baseline;
`;

const Label = styled.span`
  font-family: var(--font-bold);
  text-align: end;
  font-size: 14px;
  color: var(--text-primary);
  min-width: 80px;
`;

const FullWidthFormItem = styled(Form.Item)`
  width: 100%;
`;

const PositionFieldContent = styled.div`
  width: 100%;
`;

type SelectOption = {
  value: number;
  label: string;
  name?: string;
};

type Props = {
  companiesOptions: SelectOption[];
  disabledCompanySelector: boolean;
  disableEmailEdit: boolean;
  disabledHoldingSelector: boolean;
  isAdminUser: boolean;
  positionOptions: SelectOption[];
  selectedPosition: PositionRightDataType | null;
  onCompanyClear: () => void;
  onCompanySelect: (value: number) => void;
  onHoldingClear: () => void;
  onHoldingSelect: (value: number) => void;
};

const UserManagementBasicDataSection = ({
  companiesOptions,
  disabledCompanySelector,
  disableEmailEdit,
  disabledHoldingSelector,
  isAdminUser,
  positionOptions,
  selectedPosition,
  onCompanyClear,
  onCompanySelect,
  onHoldingClear,
  onHoldingSelect,
}: Props) => {
  const { data: holdingOptions } = useQuery({
    queryKey: ['holdings'],
    queryFn: () => getHoldings(),
    select: (data) =>
      (data || []).map((holding: HoldingType) => ({
        value: holding.id,
        label: holding.name,
      })),
    enabled: isAdminUser,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return (
    <Container>
      <Header>Basic Info</Header>
      <FieldRow>
        <Label>First Name</Label>
        <FullWidthFormItem name="first_name" rules={[{ required: true, message: 'First name is required' }]}>
          <Input placeholder={"Enter the user's first name"} data-test-id="user-management-first-name-input" />
        </FullWidthFormItem>
      </FieldRow>
      <FieldRow>
        <Label>Last Name</Label>
        <FullWidthFormItem name="last_name" rules={[{ required: true, message: 'Last name is required' }]}>
          <Input placeholder={"Enter the user's last name"} data-test-id="user-management-last-name-input" />
        </FullWidthFormItem>
      </FieldRow>
      <FieldRow>
        <Label>Email</Label>
        <FullWidthFormItem
          name="email"
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Enter a valid email' },
          ]}
        >
          <Input
            placeholder={"Enter the user's email"}
            disabled={disableEmailEdit}
            data-test-id="user-management-email-input"
          />
        </FullWidthFormItem>
      </FieldRow>
      <GradientDivider className={'thin'} />
      <BaselineFieldRow>
        <Label>Position</Label>
        <PositionFieldContent>
          <FullWidthFormItem name="position_id">
            <Select
              className={'rectangular'}
              placeholder={"Select the user's position"}
              allowClear
              options={positionOptions}
              dataTestId="user-management-position-select"
            />
          </FullWidthFormItem>
          {isAdminUser && <UserManagementPositionPermissions position={selectedPosition} />}
        </PositionFieldContent>
      </BaselineFieldRow>
      {isAdminUser && (
        <>
          <FieldRow>
            <Label>Company</Label>
            <FullWidthFormItem name="company_id">
              <Select
                showSearch
                allowClear
                className={'rectangular'}
                placeholder={'Select a Company'}
                filterOption={(input, option) => option?.name?.toLowerCase().includes(input.toLowerCase())}
                disabled={!companiesOptions?.length || disabledCompanySelector}
                options={companiesOptions}
                onSelect={onCompanySelect}
                onClear={onCompanyClear}
                dataTestId="user-management-company-select"
              />
            </FullWidthFormItem>
          </FieldRow>
          <FieldRow>
            <Label>Owner</Label>
            <FullWidthFormItem name="holding_id">
              <Select
                showSearch
                allowClear
                className={'rectangular'}
                placeholder={'Select an Owner'}
                disabled={!holdingOptions?.length || disabledHoldingSelector}
                options={holdingOptions}
                onSelect={onHoldingSelect}
                onClear={onHoldingClear}
                dataTestId="user-management-owner-select"
              />
            </FullWidthFormItem>
          </FieldRow>
        </>
      )}
      <FieldRow>
        <Label>Active</Label>
        <Form.Item name="is_active" valuePropName="checked">
          <Checkbox className={'green'} data-test-id="user-management-active-checkbox" />
        </Form.Item>
      </FieldRow>
    </Container>
  );
};

export default UserManagementBasicDataSection;
